import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Loader2, ShieldCheck, Mail, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { fmtDay } from './WindowBanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GMAIL = /^[a-z0-9._%+-]+@gmail\.com$/i;

export const LeadCheckoutModal = ({ pkg, config, open, onOpenChange }) => {
  const { user, getAuthHeader } = useAuth();
  const [email, setEmail] = useState(user?.email?.toLowerCase().endsWith('@gmail.com') ? user.email : '');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [acks, setAcks] = useState(() => (config?.acknowledgements || []).map(() => false));
  const [busy, setBusy] = useState(false);
  if (!pkg) return null;
  const emailOk = GMAIL.test(email.trim());
  const emailsMatch = confirmEmail.trim().toLowerCase() === email.trim().toLowerCase();
  const ready = emailOk && !!confirmEmail.trim() && emailsMatch && acks.length === config.acknowledgements.length && acks.every(Boolean) && config.window.is_open;

  const pay = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/lead-store/checkout`, { package_key: pkg.key, delivery_email: email.trim(), delivery_email_confirm: confirmEmail.trim(), acknowledgements: acks, origin_url: window.location.origin }, getAuthHeader());
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to start checkout');
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-[560px]" data-testid="lead-checkout-modal">
        <DialogHeader>
          <DialogTitle className="font-['Manrope'] text-xl font-extrabold text-[#2B3674]">Review your order</DialogTitle>
        </DialogHeader>

        <div className="rounded-2xl bg-gradient-to-br from-[#0B1437] to-[#111C44] p-5 text-white">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#00E5FF]">{config.product_name}</p>
              <p className="mt-1 font-['Manrope'] text-2xl font-extrabold">{pkg.name}</p>
              <p className="mt-1 text-xs text-white/60">Est. <span className="font-bold text-white">{pkg.estimate.label}</span> · {config.states.length}-state campaign · Fulfillment begins {fmtDay(config.window.fulfillment_starts)}</p>
            </div>
            <p className="font-['Manrope'] text-3xl font-extrabold" data-testid="lead-checkout-price">{pkg.price_display}</p>
          </div>
          <p className="mt-3 rounded-xl bg-white/10 px-3 py-2 text-[11px] leading-relaxed text-white/70">{config.quantity_disclosure}</p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="delivery-email" className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-[#2447D6]" /> Best Google email for lead delivery</Label>
          <Input id="delivery-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@gmail.com" className={email && !emailOk ? 'border-red-500' : ''} data-testid="lead-checkout-email" />
          <p className="text-[11px] text-[#A3AED0]">Must end in <span className="font-bold">@gmail.com</span>. Real-time lead emails and your dedicated Google Sheet (set up by our marketing team) go here.</p>
          {email && !emailOk && <p className="text-[11px] font-semibold text-red-500" data-testid="lead-checkout-email-error">Enter a Google account ending in @gmail.com</p>}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="delivery-email-confirm" className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-[#2447D6]" /> Re-type your Google email to confirm</Label>
          <Input id="delivery-email-confirm" type="email" value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} onPaste={(e) => e.preventDefault()} placeholder="you@gmail.com" autoComplete="off" className={confirmEmail && !emailsMatch ? 'border-red-500' : confirmEmail && emailsMatch && emailOk ? 'border-emerald-500' : ''} data-testid="lead-checkout-email-confirm" />
          {confirmEmail && !emailsMatch
            ? <p className="text-[11px] font-semibold text-red-500" data-testid="lead-checkout-email-mismatch">Emails don't match — leads go to exactly what you type, so please double-check</p>
            : <p className="text-[11px] text-[#A3AED0]">Type it again (pasting is disabled) so a typo can't send your leads to the wrong inbox.</p>}
        </div>

        <div className="space-y-3 rounded-2xl border border-[#E9EDF7] bg-[#F4F7FE] p-4">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#A3AED0]">Required acknowledgements</p>
          {config.acknowledgements.map((text, i) => (
            <label key={i} className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-[#2B3674]" data-testid={`lead-checkout-ack-${i}`}>
              <Checkbox checked={acks[i]} onCheckedChange={(v) => setAcks((a) => a.map((x, j) => (j === i ? !!v : x)))} className="mt-0.5" data-testid={`lead-checkout-ack-checkbox-${i}`} />
              <span>{text}</span>
            </label>
          ))}
          <p className="text-[11px] text-[#707EAE]">Eligible states: {config.states.join(', ')}. Holding all {config.states.length} licenses lets you work every lead you receive.</p>
        </div>

        <button type="button" onClick={pay} disabled={!ready || busy} className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#23ACD5] to-[#00E5FF] py-3.5 font-['Manrope'] text-sm font-extrabold text-[#06263A] shadow-[0_8px_28px_rgba(35,172,213,0.45)] transition-transform hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100" data-testid="lead-checkout-pay-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
          {config.window.is_open ? `Continue to secure checkout · ${pkg.price_display}` : 'Purchasing is closed'}
        </button>
        <p className="flex items-center justify-center gap-1.5 text-[11px] text-[#A3AED0]"><ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> Payments processed securely by Stripe. ATLAS never stores card details.</p>
      </DialogContent>
    </Dialog>
  );
};
